import { useCallback, useState } from "Shared/haunted/CustomHooks";
import dayjs, { Dayjs } from "dayjs";
import { City } from "Components/searchbox/searchTab/flights/RoutePickerModel";
import {
	CurrentSearch,
	DEFAULT_CURRENT_SEARCH,
	SearchPassengers,
} from "Shared/managers/current-search-manager/CurrentSearch";
import { StoredSearch } from "Components/searchbox/searchTab/flights/usePreviousSearch";
import { useReduxState } from "Shared/redux/useReduxState";

export interface CurrentSearchManager {
	currentSearch: CurrentSearch;
	addPromoCode: (code: string) => void;
	reloadSearch: (storedSearch: StoredSearch, promoCodes: string[]) => void;
	removePromoCode: (code: string) => void;
	resetSearch: () => void;
	setDepartureDate: (date: Dayjs) => void;
	setDestinationCity: (city: City) => void;
	setIsOneWay: (isOneWay: boolean) => void;
	setIsReloading: (isReloading: boolean) => void;
	setOriginCity: (city: City) => void;
	setPassengers: (passengers: SearchPassengers) => void;
	setPromoCodes: (codes: string[]) => void;
	setReturnDate: (date: Dayjs) => void;
	setUseAmericanMiles: (use: boolean) => void;
	swapStations: () => void;
}

export const useCurrentSearchManager = (): CurrentSearchManager => {
	const [countries] = useReduxState("countries");
	const [currentSearch, setCurrentSearch] = useState<CurrentSearch>(DEFAULT_CURRENT_SEARCH);

	// Actions with racing condition protection
	const setOriginCity = useCallback((city: City) => {
		setCurrentSearch((prev) => {
			if (prev.originCity === city) return prev;
			return {
				...prev,
				originCity: city,
				destinationCity: undefined,
				departureDate: undefined,
				returnDate: undefined,
				passengers: DEFAULT_CURRENT_SEARCH.passengers,
			};
		});
	}, []);

	const setDestinationCity = useCallback((city: City) => {
		setCurrentSearch((prev) => {
			if (prev.destinationCity === city) return prev;
			return {
				...prev,
				destinationCity: city,
				departureDate: undefined,
				returnDate: undefined,
			};
		});
	}, []);

	const setDepartureDate = useCallback((date: Dayjs) => {
		setCurrentSearch((prev) => {
			if (prev.departureDate === date) return prev;
			return {
				...prev,
				departureDate: date,
				returnDate: prev.isOneWay ? undefined : prev.returnDate,
			};
		});
	}, []);

	const setReturnDate = useCallback((date: Dayjs) => {
		setCurrentSearch((prev) => {
			if (prev.returnDate === date) return prev;
			return {
				...prev,
				returnDate: date,
			};
		});
	}, []);

	const setPassengers = useCallback((passengers: SearchPassengers) => {
		setCurrentSearch((prev) => {
			if (prev.passengers === passengers) return prev;
			return {
				...prev,
				passengers,
			};
		});
	}, []);

	const setIsOneWay = useCallback((isOneWay: boolean) => {
		setCurrentSearch((prev) => {
			if (prev.isOneWay === isOneWay) return prev;
			return {
				...prev,
				isOneWay,
				returnDate: isOneWay ? undefined : prev.returnDate,
			};
		});
	}, []);

	const setPromoCodes = useCallback((codes: string[]) => {
		setCurrentSearch((prev) => {
			if (prev.promoCodes.length === codes.length && prev.promoCodes.every((code) => codes.includes(code))) {
				return prev;
			}
			return {
				...prev,
				promoCodes: codes,
			};
		});
	}, []);

	const setUseAmericanMiles = useCallback((use: boolean) => {
		setCurrentSearch((prev) => {
			if (prev.useAmericanMiles === use) return prev;
			return {
				...prev,
				useAmericanMiles: use,
			};
		});
	}, []);

	const setIsReloading = useCallback((isReloading: boolean) => {
		setCurrentSearch((prev) => {
			if (prev.isReloading === isReloading) return prev;
			return {
				...prev,
				isReloading,
			};
		});
	}, []);

	const addPromoCode = useCallback((code: string) => {
		setCurrentSearch((prev) => {
			if (prev.promoCodes.includes(code)) return prev;
			return {
				...prev,
				promoCodes: [...prev.promoCodes, code],
			};
		});
	}, []);

	const removePromoCode = useCallback((code: string) => {
		setCurrentSearch((prev) => {
			if (!prev.promoCodes.includes(code)) return prev;
			return {
				...prev,
				promoCodes: prev.promoCodes.filter((c) => c !== code),
			};
		});
	}, []);

	const swapStations = useCallback(() => {
		setCurrentSearch((prev) => {
			if (!prev.originCity || !prev.destinationCity) return prev;

			return {
				...prev,
				originCity: prev.destinationCity,
				destinationCity: prev.originCity,
				departureDate: undefined,
				returnDate: undefined,
			};
		});
	}, []);

	const resetSearch = useCallback(() => {
		setCurrentSearch(DEFAULT_CURRENT_SEARCH);
	}, []);

	const reloadSearch = useCallback(
		(storedSearch: StoredSearch, promoCodes: string[]) => {
			setCurrentSearch((prev) => {
				return {
					...prev,
					isReloading: true,
					departureDate: dayjs(storedSearch.departureDate),
					destinationCity: countries
						.flatMap((c) => c.cities)
						.find((c) => c.code === storedSearch.destination),
					isOneWay: storedSearch.isOneWay,
					originCity: countries.flatMap((c) => c.cities).find((c) => c.code === storedSearch.origin),
					passengers: {
						adults: storedSearch.passengers.adults,
						children: storedSearch.passengers.children,
						infants: storedSearch.passengers.infants,
						teens: storedSearch.passengers.teens,
					},
					returnDate: storedSearch.returnDate ? dayjs(storedSearch.returnDate) : undefined,
					promoCodes,
				};
			});
		},
		[countries]
	);

	return {
		currentSearch,
		setOriginCity,
		setDestinationCity,
		setDepartureDate,
		setReturnDate,
		setPassengers,
		setIsOneWay,
		setPromoCodes,
		setUseAmericanMiles,
		setIsReloading,
		addPromoCode,
		removePromoCode,
		swapStations,
		resetSearch,
		reloadSearch,
	};
};
